import { Select, Spin } from "antd";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import toast from "react-hot-toast";
import { IoCloseSharp } from "react-icons/io5";
import { Button, Modal, ModalBody, Table } from "reactstrap";
import { getAllManufacturers } from "../../api/pagination";
import { importProductsFromExcel } from "../../api/products.api";
import { uploadExcelFile } from "../../firebase/utility";
import GeneralButton from "../button/GeneralButton";
import Separator from "../Separator";

const ImportProductsModal = ({ modal, toggle, gettingProducts }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState("");
  const [selectedMfg, setSelectedMfg] = useState("");
  const [manufacturers, setManufacturers] = useState([]);
  const [mfgLoading, setMfgLoading] = useState(false);
  const [searchedMfg, setSearchedMfg] = useState([]);

  useEffect(() => {
    if (modal) {
      fetchManufacturers();
    } else {
      resetState();
    }
  }, [modal]);

  const fetchManufacturers = (search = "") => {
    setMfgLoading(true);
    getAllManufacturers(1, search, 50, (res) => {
      if (res.status === 200) {
        if (search) {
          setSearchedMfg(res.data.data);
        } else {
          setManufacturers(res.data.data);
          setSearchedMfg([]);
        }
      }
      setMfgLoading(false);
    });
  };

  const fetchManufacturersDebounced = useCallback(
    debounce((value) => {
      fetchManufacturers(value);
    }, 500),
    []
  );

  const handleManufacturerSearch = (value) => {
    if (value) {
      fetchManufacturersDebounced(value);
    } else {
      setSearchedMfg([]);
    }
  };

  const resetState = () => {
    setFile(null);
    setLoading(false);
    setPreviewData([]);
    setIsPreviewMode(false);
    setUploadedFilePath("");
    setSelectedMfg("");
    setSearchedMfg([]);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUploadAndPreview = async () => {
    if (!file) {
      toast.error("Please select an Excel file first.");
      return;
    }
    if (!selectedMfg) {
      toast.error("Please select a manufacturer.");
      return;
    }

    setLoading(true);
    try {
      const { url, path } = await uploadExcelFile(file);
      setUploadedFilePath(path);

      const payload = {
        filePath: path,
        manufacturerId: selectedMfg,
        previewOnly: true,
      };


      importProductsFromExcel(payload, (res) => {
        if (res.status === 200) {
          // The API returns { success, validRows, invalidRows, totalProcessed, ... }
          setPreviewData(res.data || {});
          setIsPreviewMode(true);
          toast.success("Products preview loaded successfully.");
        } else {
          toast.error(res.message || "Failed to load preview.");
        }
        setLoading(false);
      });
    } catch (error) {
      toast.error("Error uploading file: " + error.message);
      setLoading(false);
    }
  };

  const handleFinalImport = () => {
    if (!uploadedFilePath || !selectedMfg) {
      toast.error("Missing file path or manufacturer.");
      return;
    }

    setLoading(true);
    const payload = {
      filePath: uploadedFilePath,
      manufacturerId: selectedMfg,
      previewOnly: false,
    };

    importProductsFromExcel(payload, (res) => {
      if (res.status === 200) {
        toast.success("Products imported successfully!");
        gettingProducts();
        toggle();
      } else {
        toast.error(res.message || "Failed to import products.");
      }
      setLoading(false);
    });
  };

  const validRows = previewData?.validRows || [];
  const invalidRows = previewData?.invalidRows || [];

  return (
    <Modal isOpen={modal} toggle={toggle} centered size="xl">
      <ModalBody className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="primary-color">Import Products from Excel</h4>
          <IoCloseSharp size={"30"} onClick={toggle} className="pointer" />
        </div>
        <Separator />

        <div className="mt-4">
          {!isPreviewMode ? (
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Select Manufacturer</label>
                <Select
                  size="large"
                  showSearch
                  placeholder={"Search Manufacturer"}
                  filterOption={false}
                  onSelect={(val) => setSelectedMfg(val)}
                  onSearch={handleManufacturerSearch}
                  className="w-100"
                  options={(searchedMfg?.length > 0
                    ? searchedMfg
                    : manufacturers
                  )?.map((option) => ({
                    value: option._id,
                    label: `${option.id}-${option.name}`,
                  }))}
                  loading={mfgLoading}
                  notFoundContent={
                    mfgLoading ? <Spin size="small" /> : "No manufacturers found"
                  }
                  value={selectedMfg || undefined}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Select Excel File</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                />
              </div>
              <div className="col-12 mt-3 text-center">
                <GeneralButton
                  title={loading ? "Uploading..." : "Upload & Preview"}
                  className="blue-button"
                  width="200px"
                  loading={loading}
                  disabled={loading || !file || !selectedMfg}
                  handleClick={handleUploadAndPreview}
                />
              </div>
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Preview Products ({previewData.totalProcessed || 0} total rows processed)</h5>
                <Button color="link" onClick={() => setIsPreviewMode(false)}>
                  Back to Upload
                </Button>
              </div>

              <div className="d-flex gap-4 mb-3">
                <span className="text-success fw-bold">Valid: {previewData.validCount || 0}</span>
                <span className="text-danger fw-bold">Invalid: {previewData.invalidCount || 0}</span>
              </div>

              <div
                style={{
                  maxHeight: "600px",
                  overflow: "auto",
                  border: "1px solid #eee",
                }}
              >
                <Table striped hover responsive style={{ borderCollapse: 'separate', minWidth: '2000px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1, boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>
                    <tr>
                      <th style={{ minWidth: '60px' }}>Row</th>
                      <th style={{ minWidth: '150px' }}>SKU</th>
                      <th style={{ minWidth: '250px' }}>Title</th>
                      <th style={{ minWidth: '120px' }}>UPC</th>
                      <th style={{ minWidth: '150px' }}>MFG Part</th>
                      <th style={{ minWidth: '150px' }}>Vendor Part</th>
                      <th style={{ minWidth: '150px' }}>Category</th>
                      <th style={{ minWidth: '150px' }}>Sub Category</th>
                      <th style={{ minWidth: '120px' }}>Type</th>
                      <th style={{ minWidth: '100px' }}>Cost</th>
                      <th style={{ minWidth: '100px' }}>Price</th>
                      <th style={{ minWidth: '100px' }}>MAP</th>
                      <th style={{ minWidth: '100px' }}>MSRP</th>
                      <th style={{ minWidth: '100px' }}>Markup %</th>
                      <th style={{ minWidth: '80px' }}>Qty</th>
                      <th style={{ minWidth: '80px' }}>Weight</th>
                      <th style={{ minWidth: '100px' }}>Condition</th>
                      <th style={{ minWidth: '100px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validRows.length > 0 ? (
                      validRows.map((item, index) => {
                        const product = item.product || {};
                        return (
                          <tr key={index}>
                            <td>{item.rowNumber}</td>
                            <td>{product.sku}</td>
                            <td>{product.name || product.item_title}</td>
                            <td>{product.item_upc || "N/A"}</td>
                            <td>{product.item_mfg_part || "N/A"}</td>
                            <td>{product.vendor_part || "N/A"}</td>
                            <td>{product.category?.categoryName || "N/A"}</td>
                            <td>{product.subCategory?.subCategoryName || "N/A"}</td>
                            <td>{product.productType || "N/A"}</td>
                            <td>{product.stc_cost}</td>
                            <td>{product.price || product.stc_price}</td>
                            <td>{product.stc_map || "N/A"}</td>
                            <td>{product.stc_MSRP || "N/A"}</td>
                            <td>{product.stc_markup || "N/A"}%</td>
                            <td>{product.stc_quantity}</td>
                            <td>{product.weight || "0"}</td>
                            <td>{product.mfg_condition}</td>
                            <td>{product.status}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="18" className="text-center">
                          No valid products found in the file.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {invalidRows.length > 0 && (
                <div className="mt-4">
                  <h6 className="text-danger">Invalid Rows</h6>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    <Table size="sm" striped>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invalidRows.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.rowNumber}</td>
                            <td>{row.errors?.join(", ") || "Unknown error"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              <div className="mt-4 text-center">
                <GeneralButton
                  title={loading ? "Importing..." : "Confirm Import"}
                  className="blue-button"
                  width="200px"
                  loading={loading}
                  disabled={loading || validRows.length === 0}
                  handleClick={handleFinalImport}
                />
              </div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ImportProductsModal;
